import { accessoryDefinitions } from "./accessories";
import type {
  AccessoryId,
  DecorationSelections,
  DecorationSlot,
  DecorationTransforms,
  EditSettings,
  PreparedCard,
  PreparedProject,
  Transform,
} from "../types";
import { finishAlphaMatte } from "./matte";
import { createBaseCard, createNameLayer } from "./preprocess";

const DATABASE = "kotodeco-static";
const STORE = "prepared-sessions";
const LATEST = "latest";
const SETTINGS_KEY = "kotodeco-static:settings";
const MAX_AGE_MS = 12 * 60 * 60 * 1000;

type StoredCard = {
  source: Blob;
  base: Blob;
  nameGlyphs: Blob;
  hero: Blob;
  nameBox: PreparedCard["nameBox"];
  heroHead: PreparedCard["heroHead"];
  accent: string;
};

type StoredProject = {
  id: typeof LATEST;
  createdAt: number;
  fileName: string;
  sourceWidth: number;
  sourceHeight: number;
  modelMode: PreparedProject["modelMode"];
  preprocessMs: number;
  cards: StoredCard[];
};

function openDatabase() {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DATABASE, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) {
        request.result.createObjectStore(STORE, { keyPath: "id" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("編集キャッシュを開けませんでした"));
  });
}

function transaction<T>(
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore) => IDBRequest<T>,
) {
  return openDatabase().then((database) => new Promise<T>((resolve, reject) => {
    const tx = database.transaction(STORE, mode);
    const request = action(tx.objectStore(STORE));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("編集キャッシュを更新できませんでした"));
    tx.oncomplete = () => database.close();
    tx.onerror = () => {
      database.close();
      reject(tx.error ?? new Error("編集キャッシュを更新できませんでした"));
    };
  }));
}

function canvasBlob(canvas: HTMLCanvasElement) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => blob ? resolve(blob) : reject(new Error("編集レイヤーを保存できませんでした")),
      "image/png",
    );
  });
}

async function blobCanvas(blob: Blob) {
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("編集レイヤーを復元できませんでした");
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(bitmap, 0, 0);
  bitmap.close();
  return canvas;
}

function finishRestoredHero(canvas: HTMLCanvasElement) {
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) return canvas;
  const image = context.getImageData(0, 0, canvas.width, canvas.height);
  const alpha = new Uint8ClampedArray(canvas.width * canvas.height);
  for (let offset = 3; offset < image.data.length; offset += 4) {
    alpha[(offset - 3) / 4] = image.data[offset];
  }
  const finished = finishAlphaMatte(alpha, canvas.width, canvas.height);
  for (let offset = 3; offset < image.data.length; offset += 4) {
    image.data[offset] = finished[(offset - 3) / 4];
  }
  context.putImageData(image, 0, 0);
  return canvas;
}

export function persistSettings(settings: EditSettings) {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // Private browsing can deny storage. Editing itself should keep working.
  }
}

function restoredSettings(cardCount: number) {
  try {
    const parsed = JSON.parse(localStorage.getItem(SETTINGS_KEY) ?? "") as Partial<EditSettings> & {
      accessoryIds?: AccessoryId[];
      accessoryTransforms?: Transform[];
    };
    const slots: readonly DecorationSlot[] = ["head", "face", "behind", "card"];
    const normalizeTransform = (value?: Partial<Transform>): Transform => ({
      x: Number.isFinite(value?.x) ? Number(value?.x) : 0,
      y: Number.isFinite(value?.y) ? Number(value?.y) : 0,
      scale: Number.isFinite(value?.scale) ? Number(value?.scale) : 1,
      angle: Number.isFinite(value?.angle) ? Number(value?.angle) : 0,
      flipX: value?.flipX === true,
    });
    const blankSelections = (): DecorationSelections => ({
      head: Array.from({ length: cardCount }, () => "none"),
      face: Array.from({ length: cardCount }, () => "none"),
      behind: Array.from({ length: cardCount }, () => "none"),
      card: Array.from({ length: cardCount }, () => "none"),
    });
    const blankTransforms = (): DecorationTransforms => ({
      head: Array.from({ length: cardCount }, () => normalizeTransform()),
      face: Array.from({ length: cardCount }, () => normalizeTransform()),
      behind: Array.from({ length: cardCount }, () => normalizeTransform()),
      card: Array.from({ length: cardCount }, () => normalizeTransform()),
    });
    if (
      parsed
      && Array.isArray(parsed.nameModes)
      && parsed.nameModes.length === cardCount
      && Array.isArray(parsed.heroTransforms)
      && parsed.heroTransforms.length === cardCount
    ) {
      let decorations = parsed.decorations;
      let decorationTransforms = parsed.decorationTransforms;
      if (
        !decorations
        || !decorationTransforms
        || slots.some((slot) =>
          !Array.isArray(decorations?.[slot])
          || decorations[slot].length !== cardCount
          || !Array.isArray(decorationTransforms?.[slot])
          || decorationTransforms[slot].length !== cardCount)
      ) {
        decorations = blankSelections();
        decorationTransforms = blankTransforms();
        parsed.accessoryIds?.forEach((id, index) => {
          if (!id || id === "none" || !accessoryDefinitions[id]) return;
          const replacement: Partial<Record<AccessoryId, AccessoryId>> = {
            headphones: "fitPastelHeadphones",
            djHeadphones: "fitDjHeadphones",
            headset: "fitStudioHeadset",
          };
          const migratedId = replacement[id] ?? id;
          if (migratedId === "none") return;
          const slot = accessoryDefinitions[migratedId].slot;
          decorations![slot][index] = migratedId;
          decorationTransforms![slot][index] = normalizeTransform(parsed.accessoryTransforms?.[index]);
        });
      } else {
        decorationTransforms = Object.fromEntries(slots.map((slot) => [
          slot,
          decorationTransforms![slot].map(normalizeTransform),
        ])) as DecorationTransforms;
      }
      return {
        ...parsed,
        decorations,
        decorationTransforms,
        heroTransforms: parsed.heroTransforms.map(normalizeTransform),
      } as EditSettings;
    }
  } catch {
    // Ignore expired/corrupt state.
  }
  return null;
}

export async function persistPreparedProject(project: PreparedProject) {
  if (!("indexedDB" in window)) return;
  const cards = await Promise.all(project.cards.map(async (card): Promise<StoredCard> => ({
    source: await canvasBlob(card.source),
    base: await canvasBlob(card.base),
    nameGlyphs: await canvasBlob(card.nameGlyphs),
    hero: await canvasBlob(card.hero),
    nameBox: card.nameBox,
    heroHead: card.heroHead,
    accent: card.accent,
  })));
  const record: StoredProject = {
    id: LATEST,
    createdAt: Date.now(),
    fileName: project.fileName,
    sourceWidth: project.sourceWidth,
    sourceHeight: project.sourceHeight,
    modelMode: project.modelMode,
    preprocessMs: project.preprocessMs,
    cards,
  };
  await transaction("readwrite", (store) => store.put(record));
}

export async function restorePreparedSession() {
  if (!("indexedDB" in window)) return null;
  const stored = await transaction<StoredProject | undefined>("readonly", (store) => store.get(LATEST));
  if (!stored) return null;
  if (Date.now() - stored.createdAt > MAX_AGE_MS) {
    await transaction("readwrite", (store) => store.delete(LATEST));
    localStorage.removeItem(SETTINGS_KEY);
    return null;
  }
  const cards = await Promise.all(stored.cards.map(async (card): Promise<PreparedCard> => {
    const [source, hero] = await Promise.all([
      blobCanvas(card.source),
      blobCanvas(card.hero),
    ]);
    return {
      source,
      base: createBaseCard(source, card.nameBox),
      nameGlyphs: createNameLayer(source, card.nameBox),
      nameBox: card.nameBox,
      heroSource: finishRestoredHero(hero),
      hero,
      heroAlpha: new Uint8ClampedArray(),
      heroHead: card.heroHead,
      accent: card.accent,
    };
  }));
  const project: PreparedProject = {
    fileName: stored.fileName,
    sourceWidth: stored.sourceWidth,
    sourceHeight: stored.sourceHeight,
    cards,
    modelMode: stored.modelMode,
    preprocessMs: stored.preprocessMs,
  };
  return { project, settings: restoredSettings(cards.length) };
}
